import { useEffect, useId, useRef, useState } from 'react'
import { FaChevronDown, FaXmark } from 'react-icons/fa6'

const OTHER_PRODUCT_OPTION = 'Other / General Enquiry'

function formatSelectionLabel(selected, placeholder) {
  if (!selected.length) {
    return placeholder
  }

  if (selected.length === 1) {
    return selected[0]
  }

  if (selected.length === 2) {
    return `${selected[0]}, ${selected[1]}`
  }

  return `${selected.length} products selected`
}

function ProductAccordionSelect({
  id,
  name,
  value = [],
  onChange,
  options,
  placeholder = 'Select one or more products',
  disabled = false,
  hasError = false,
}) {
  const listboxId = useId()
  const rootRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [openGroupIndex, setOpenGroupIndex] = useState(null)

  const selectedProducts = Array.isArray(value) ? value : value ? [value] : []

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false)
        setOpenGroupIndex(null)
      }
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        setOpenGroupIndex(null)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const emitChange = (nextValue) => {
    onChange({
      target: {
        name,
        value: nextValue,
      },
    })
  }

  const handleTogglePanel = () => {
    if (disabled) {
      return
    }

    setIsOpen((current) => {
      if (current) {
        setOpenGroupIndex(null)
      }
      return !current
    })
  }

  const handleClosePanel = () => {
    setIsOpen(false)
    setOpenGroupIndex(null)
  }

  const handleToggleGroup = (groupIndex) => {
    setOpenGroupIndex((current) => (current === groupIndex ? null : groupIndex))
  }

  const handleToggleProduct = (productName) => {
    const isSelected = selectedProducts.includes(productName)
    const nextValue = isSelected
      ? selectedProducts.filter((item) => item !== productName)
      : [...selectedProducts, productName]

    emitChange(nextValue)
  }

  const handleRemoveProduct = (event, productName) => {
    event.stopPropagation()
    emitChange(selectedProducts.filter((item) => item !== productName))
  }

  const displayValue = formatSelectionLabel(selectedProducts, placeholder)

  return (
    <div
      className={`product-accordion-select${isOpen ? ' product-accordion-select--open' : ''}${
        hasError ? ' product-accordion-select--error' : ''
      }`}
      ref={rootRef}
    >
      <button
        type="button"
        id={id}
        className="product-accordion-select__trigger"
        onClick={handleTogglePanel}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
      >
        <span
          className={
            selectedProducts.length
              ? 'product-accordion-select__value'
              : 'product-accordion-select__placeholder'
          }
        >
          {displayValue}
        </span>
        <FaChevronDown className="product-accordion-select__chevron" aria-hidden="true" />
      </button>

      {selectedProducts.length > 0 ? (
        <div className="product-accordion-select__tags" aria-label="Selected products">
          {selectedProducts.map((product) => (
            <span className="product-accordion-select__tag" key={product}>
              {product}
              <button
                type="button"
                className="product-accordion-select__tag-remove"
                onClick={(event) => handleRemoveProduct(event, product)}
                disabled={disabled}
                aria-label={`Remove ${product}`}
              >
                <FaXmark aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {isOpen ? (
        <div className="product-accordion-select__panel" id={listboxId} role="listbox" aria-label="Product list">
          <div className="product-accordion-select__panel-body">
            {options.map((group, groupIndex) => {
              const isGroupOpen = openGroupIndex === groupIndex
              const groupSelectedCount = group.options.filter((option) =>
                selectedProducts.includes(option),
              ).length

              return (
                <div
                  className={`product-accordion-select__group${isGroupOpen ? ' product-accordion-select__group--open' : ''}`}
                  key={group.label}
                >
                  <button
                    type="button"
                    className="product-accordion-select__group-toggle"
                    onClick={() => handleToggleGroup(groupIndex)}
                    aria-expanded={isGroupOpen}
                  >
                    <span className="product-accordion-select__group-label">
                      {group.label}
                      {groupSelectedCount > 0 ? (
                        <span className="product-accordion-select__group-count">{groupSelectedCount}</span>
                      ) : null}
                    </span>
                    <FaChevronDown className="product-accordion-select__group-chevron" aria-hidden="true" />
                  </button>

                  {isGroupOpen ? (
                    <ul className="product-accordion-select__list">
                      {group.options.map((option) => {
                        const isSelected = selectedProducts.includes(option)
                        const optionId = `${id}-${option.replace(/\s+/g, '-').toLowerCase()}`

                        return (
                          <li key={option}>
                            <label className="product-accordion-select__check" htmlFor={optionId}>
                              <input
                                id={optionId}
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleProduct(option)}
                              />
                              <span className="product-accordion-select__check-box" aria-hidden="true" />
                              <span className="product-accordion-select__check-label">{option}</span>
                            </label>
                          </li>
                        )
                      })}
                    </ul>
                  ) : null}
                </div>
              )
            })}

            <label
              className={`product-accordion-select__other-check${
                selectedProducts.includes(OTHER_PRODUCT_OPTION)
                  ? ' product-accordion-select__other-check--selected'
                  : ''
              }`}
              htmlFor={`${id}-other`}
            >
              <input
                id={`${id}-other`}
                type="checkbox"
                checked={selectedProducts.includes(OTHER_PRODUCT_OPTION)}
                onChange={() => handleToggleProduct(OTHER_PRODUCT_OPTION)}
              />
              <span className="product-accordion-select__check-box" aria-hidden="true" />
              <span className="product-accordion-select__check-label">{OTHER_PRODUCT_OPTION}</span>
            </label>
          </div>

          <div className="product-accordion-select__footer">
            <span className="product-accordion-select__footer-count">
              {selectedProducts.length} product{selectedProducts.length === 1 ? '' : 's'} selected
            </span>
            <button type="button" className="product-accordion-select__done" onClick={handleClosePanel}>
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ProductAccordionSelect
